import type { Lang } from '../i18n/index.js';
import { theme } from '../theme.js';

/**
 * Shown to someone who has signed in successfully but is not yet linked to any
 * client company.
 *
 * This is a normal state, not an error: an account exists before its
 * permissions do. It says who they are signed in as, because "I have no access"
 * is very often "I signed in with the wrong account".
 */
export function NoAccessScreen({ lang, email }: { lang: Lang; email: string | null }) {
  const ar = lang === 'ar';

  const copy = ar
    ? {
        title: 'لا توجد مؤسسات متاحة لك بعد',
        body: 'تم تسجيل دخولك بنجاح، لكن حسابك غير مرتبط بأي مؤسسة حتى الآن. يقوم فريق CFO Advisory and Accountancy بمنح هذا الوصول.',
        signedIn: 'أنت مسجّل الدخول باسم',
        wrongAccount: 'إن لم يكن هذا حسابك الصحيح، سجّل الخروج وحاول مرة أخرى.',
        signOut: 'تسجيل الخروج',
      }
    : {
        title: 'No organisations available to you yet',
        body: 'You are signed in, but your account is not linked to any client organisation. CFO Advisory and Accountancy grant that access.',
        signedIn: 'Signed in as',
        wrongAccount: 'If this is not the right account, sign out and try again.',
        signOut: 'Sign out',
      };

  return (
    <div
      dir={ar ? 'rtl' : 'ltr'}
      style={{
        height: '100vh',
        display: 'grid',
        placeItems: 'center',
        background: theme.canvas,
        fontFamily: ar ? theme.fontArabic : theme.fontBody,
        color: theme.text,
        padding: '24px',
      }}
    >
      <div
        style={{
          maxWidth: '440px',
          background: theme.surface,
          borderRadius: theme.radiusCard,
          boxShadow: theme.shadowCard,
          padding: '32px',
          textAlign: ar ? 'right' : 'left',
        }}
      >
        <h1 style={{ margin: 0, fontSize: '19px', fontWeight: 600, letterSpacing: '-.01em' }}>
          {copy.title}
        </h1>
        <p style={{ color: theme.textMuted, fontSize: '13.5px', lineHeight: 1.65, marginTop: '12px' }}>
          {copy.body}
        </p>

        {email && (
          <div
            style={{
              marginTop: '20px',
              padding: '12px 14px',
              background: theme.surfaceMuted,
              border: `1px solid ${theme.border}`,
              borderRadius: theme.radiusControl,
              fontSize: '12.5px',
            }}
          >
            <span style={{ color: theme.textMuted }}>{copy.signedIn} </span>
            <span style={{ fontWeight: 600 }} dir="ltr">
              {email}
            </span>
          </div>
        )}

        <p style={{ color: theme.textSubtle, fontSize: '12px', lineHeight: 1.6, marginTop: '14px' }}>
          {copy.wrongAccount}
        </p>

        <a
          href="/logout"
          style={{
            display: 'inline-block',
            marginTop: '8px',
            background: theme.nav,
            color: '#fff',
            borderRadius: theme.radiusControl,
            padding: '9px 16px',
            fontSize: '12.5px',
            fontWeight: 600,
            textDecoration: 'none',
          }}
        >
          {copy.signOut}
        </a>
      </div>
    </div>
  );
}
